import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { WishlistHeartComponent } from '../wishlist/wishlist-heart/wishlist-heart.component';
import {
  SearchService,
  SearchListing,
  SearchFilters,
} from '../services/search.service';
import { WishlistService } from '../services/wishlist.service';
import { AuthService } from '../services/auth.service';
import { catchError, of, Subject, takeUntil } from 'rxjs';

interface Listing {
  id: number;
  title: string;
  price: number;
  category: string;
  location: string;
  date: Date;
  image: string;
  images: string[];
  currentImageIndex: number;
  currency?: string;
  subcategory?: string;
  subcategoryDisplay?: string;
  table_name?: string;
  featured?: boolean;
  new?: boolean;
  view_count?: number;
  seller?: {
    first_name: string;
    last_name: string;
    user_id?: number;
  };
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

@Component({
  selector: 'app-recent',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    FormsModule,
    WishlistHeartComponent,
  ],
  templateUrl: './recent.component.html',
  styleUrls: ['./recent.component.css'],
})
export class RecentComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  listings: Listing[] = [];
  isLoading = true;
  currentPage = 1;
  totalPages = 0;
  totalListings = 0;
  limit = 20;

  filters: SearchFilters = {};
  selectedCategory = 'all';
  selectedSort = 'newest';
  searchKeyword = '';

  loadedImages: Set<string> = new Set();

  private readonly SWIPE_THRESHOLD = 30;
  private readonly SWIPE_FAST_THRESHOLD = 20;
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3;
  private readonly SWIPE_TIME_THRESHOLD = 600;
  private readonly VERTICAL_SCROLL_THRESHOLD = 25;
  private readonly TAP_MOVEMENT_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 300;

  categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'vehicles', label: 'Vehicles' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'marketplace', label: 'Marketplace' },
    { value: 'jobs', label: 'Jobs' },
  ];

  sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'relevance', label: 'Most Relevant' },
  ];

  constructor(
    private searchService: SearchService,
    private router: Router,
    private wishlistService: WishlistService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadRecentListings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRecentListings(): void {
    this.isLoading = true;

    const searchFilters: SearchFilters = { ...this.filters };

    if (this.selectedCategory && this.selectedCategory !== 'all') {
      searchFilters['category'] = this.selectedCategory;
    }

    this.searchService
      .getRecentListings(
        searchFilters,
        this.currentPage,
        this.limit,
        this.selectedSort
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.listings = result.listings.map((listing) =>
            this.convertToListing(listing)
          );
          this.totalPages = result.pagination.pages;
          this.totalListings = result.pagination.total;
          this.isLoading = false;
          this.loadWishlistStatus(this.listings);
        },
        error: (error) => {
          console.error('Error loading recent listings:', error);
          this.isLoading = false;
          this.listings = [];
        },
      });
  }

  private convertToListing(searchListing: SearchListing): Listing {
    const images = searchListing.images || [];
    const firstImage =
      images.length > 0
        ? images[0]
        : this.getDefaultImage(searchListing.category);

    const converted: Listing = {
      id: searchListing.id,
      title: searchListing.title,
      price: searchListing.price,
      category: this.formatCategoryName(searchListing.category),
      location: searchListing.location || 'Location not specified',
      date: new Date(searchListing.created_at),
      image: firstImage,
      images: images.length > 0 ? images : [firstImage],
      currentImageIndex: 0,
      currency: searchListing.currency,
      subcategory: searchListing.subcategory,
      subcategoryDisplay: this.formatSubcategoryName(searchListing.subcategory),
      table_name: this.getTableNameFromListing(searchListing),
      view_count: searchListing.view_count || 0,
      seller: searchListing.seller
        ? {
            first_name: searchListing.seller.first_name || 'Unknown',
            last_name: searchListing.seller.last_name || '',
            user_id: searchListing.seller.id || searchListing.seller.user_id,
          }
        : undefined,
      preloadedImages: new Set([0]),
      isImageLoading: false,
      showCarouselControls: false,
      hasShownControls: false,
      isTouchScrolling: false,
      touchMoved: false,
    };

    converted.featured = searchListing.is_featured || false;

    const daysSinceCreation = Math.floor(
      (new Date().getTime() - new Date(searchListing.created_at).getTime()) /
        (1000 * 60 * 60 * 24)
    );
    converted.new = daysSinceCreation <= 3;

    return converted;
  }

  private isWishlistButtonTouch(event: TouchEvent): boolean {
    const target = event.target as HTMLElement;
    return !!(
      target.closest('.listing-bookmark') ||
      target.closest('app-wishlist-heart')
    );
  }

  onTouchStart(event: TouchEvent, listing: Listing): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    const touch = event.touches[0];
    listing.touchStartX = touch.clientX;
    listing.touchStartY = touch.clientY;
    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
    listing.touchStartTime = Date.now();
    listing.isTouchScrolling = false;
    listing.touchMoved = false;
  }

  onTouchMove(event: TouchEvent, listing: Listing): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined
    ) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - listing.touchStartX);
    const deltaY = Math.abs(touch.clientY - listing.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      listing.touchMoved = true;
    }

    if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
      listing.isTouchScrolling = true;
    } else if (deltaX > deltaY && deltaX > 5 && listing.images.length > 1) {
      event.preventDefault();
    }

    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
  }

  onTouchEnd(event: TouchEvent, listing: Listing): void {
    if (this.isWishlistButtonTouch(event)) {
      this.resetTouchState(listing);
      return;
    }

    if (listing.images.length <= 1) {
      if (!listing.isTouchScrolling && !listing.touchMoved) {
        this.onListingClick(listing);
      }
      this.resetTouchState(listing);
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined ||
      listing.touchStartTime === undefined
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - listing.touchStartX;
    const deltaY = touch.clientY - listing.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - listing.touchStartTime;

    if (
      listing.isTouchScrolling ||
      (absDeltaY > this.VERTICAL_SCROLL_THRESHOLD && absDeltaY > absDeltaX)
    ) {
      this.resetTouchState(listing);
      return;
    }

    const velocity = deltaTime > 0 ? absDeltaX / deltaTime : 0;
    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const isQuickEnough = deltaTime < this.SWIPE_TIME_THRESHOLD;
    const isFastSwipe =
      velocity >= this.SWIPE_VELOCITY_THRESHOLD &&
      absDeltaX >= this.SWIPE_FAST_THRESHOLD;
    const isLongEnough = absDeltaX > this.SWIPE_THRESHOLD || isFastSwipe;
    const isNotTooVertical = absDeltaY < this.VERTICAL_SCROLL_THRESHOLD;

    if (
      isHorizontalSwipe &&
      isQuickEnough &&
      isLongEnough &&
      isNotTooVertical
    ) {
      event.stopPropagation();
      event.preventDefault();
      if (deltaX > 0) {
        this.previousImage(listing, event);
      } else {
        this.nextImage(listing, event);
      }
    } else if (
      !listing.touchMoved ||
      (absDeltaX <= this.TAP_MOVEMENT_THRESHOLD &&
        absDeltaY <= this.TAP_MOVEMENT_THRESHOLD &&
        deltaTime <= this.TAP_TIME_THRESHOLD)
    ) {
      this.onListingClick(listing);
    }
    this.resetTouchState(listing);
  }

  private resetTouchState(listing: Listing): void {
    setTimeout(() => {
      listing.touchStartX = undefined;
      listing.touchStartY = undefined;
      listing.touchStartTime = undefined;
      listing.lastTouchX = undefined;
      listing.lastTouchY = undefined;
      listing.isTouchScrolling = false;
      listing.touchMoved = false;
    }, 100);
  }

  onImageZoneClick(
    event: Event,
    listing: Listing,
    zone: 'prev' | 'next' | 'view'
  ): void {
    event.stopPropagation();

    if (zone === 'view') {
      this.onListingClick(listing);
    } else if (zone === 'prev') {
      this.previousImage(listing, event);
    } else if (zone === 'next') {
      this.nextImage(listing, event);
    }
  }

  previousImage(listing: Listing, event: Event): void {
    event.stopPropagation();

    if (listing.images.length <= 1) return;

    const newIndex =
      (listing.currentImageIndex - 1 + listing.images.length) %
      listing.images.length;

    this.navigateToImage(listing, newIndex);
  }

  nextImage(listing: Listing, event: Event): void {
    event.stopPropagation();

    if (listing.images.length <= 1) return;

    const newIndex = (listing.currentImageIndex + 1) % listing.images.length;

    this.navigateToImage(listing, newIndex);
  }

  private navigateToImage(listing: Listing, targetIndex: number): void {
    listing.isImageLoading = true;
    listing.currentImageIndex = targetIndex;
    listing.image = listing.images[targetIndex];

    if (!listing.preloadedImages) {
      listing.preloadedImages = new Set();
    }
    listing.preloadedImages.add(targetIndex);

    this.preloadAdjacentImages(listing, targetIndex);
  }

  private preloadAdjacentImages(listing: Listing, currentIndex: number): void {
    if (listing.images.length <= 1) return;

    const nextIndex = (currentIndex + 1) % listing.images.length;
    const prevIndex =
      (currentIndex - 1 + listing.images.length) % listing.images.length;

    if (!listing.preloadedImages?.has(nextIndex)) {
      this.preloadImage(listing.images[nextIndex]);
      listing.preloadedImages?.add(nextIndex);
    }

    if (!listing.preloadedImages?.has(prevIndex)) {
      this.preloadImage(listing.images[prevIndex]);
      listing.preloadedImages?.add(prevIndex);
    }
  }

  private preloadImage(url: string): void {
    if (this.loadedImages.has(url)) return;

    const img = new Image();
    img.src = url;
    img.onload = () => {
      this.loadedImages.add(url);
    };
    img.onerror = () => {
      console.warn(`Failed to preload image: ${url}`);
    };
  }

  onListingHover(listing: Listing): void {
    if (listing.images.length <= 1) return;

    listing.images.forEach((imageUrl, index) => {
      if (!listing.preloadedImages?.has(index)) {
        this.preloadImage(imageUrl);
        listing.preloadedImages?.add(index);
      }
    });

    if (!listing.hasShownControls) {
      listing.showCarouselControls = true;
      listing.hasShownControls = true;

      if (listing.controlsTimeout) {
        clearTimeout(listing.controlsTimeout);
      }

      listing.controlsTimeout = setTimeout(() => {
        listing.showCarouselControls = false;
      }, 2000);
    }
  }

  onListingLeave(listing: Listing): void {
    if (listing.controlsTimeout) {
      clearTimeout(listing.controlsTimeout);
    }
    listing.hasShownControls = false;
    listing.showCarouselControls = false;
  }

  hasMultipleImages(listing: Listing): boolean {
    return listing.images.length > 1;
  }

  getImageCounter(listing: Listing): string {
    return `${listing.currentImageIndex + 1}/${listing.images.length}`;
  }

  onImageLoad(imageUrl: string, listing?: Listing): void {
    this.loadedImages.add(imageUrl);
    if (listing) {
      listing.isImageLoading = false;
    }
  }

  isImageLoaded(imageUrl: string): boolean {
    return this.loadedImages.has(imageUrl);
  }

  private getTableNameFromListing(listing: SearchListing): string {
    const tableMap: { [key: string]: string } = {
      'vehicles-automobile': 'attributes_automobile',
      'vehicles-motorcycle': 'attributes_motorcycle',
      'vehicles-commercial-vehicles': 'attributes_commercial_vehicles',
      'vehicles-oldtimer': 'attributes_oldtimer',
      'vehicles-nautical': 'attributes_nautical',
      'vehicles-spare-parts': 'attributes_spare_parts',
      'vehicles-campers-caravans': 'attributes_campers_caravans',
      'vehicles-crashed-cars': 'attributes_crashed_cars',
      'vehicles-construction': 'attributes_construction',
      'vehicles-agriculture': 'attributes_agriculture',
      'real-estate-house-sale': 'attributes_house_sale',
      'real-estate-house-rent': 'attributes_house_rent',
      'real-estate-apartments-sale': 'attributes_apartments_sale',
      'real-estate-apartments-rent': 'attributes_apartments_rent',
      'real-estate-land-sale': 'attributes_land_sale',
      'real-estate-land-rent': 'attributes_land_rent',
      'real-estate-business-office-sale': 'attributes_business_office_sale',
      'real-estate-business-office-rent': 'attributes_business_office_rent',
      'real-estate-garage-sale': 'attributes_garage_sale',
      'real-estate-garage-rent': 'attributes_garage_rent',
      'real-estate-luxury-real-estate-sale':
        'attributes_luxury_real_estate_sale',
      'real-estate-luxury-real-estate-rent':
        'attributes_luxury_real_estate_rent',
      'real-estate-room-rent': 'attributes_room_rent',
      'real-estate-looking-for-roommate': 'attributes_roommate',
      'marketplace-home': 'attributes_home',
      'marketplace-electronics': 'attributes_electronics',
      'marketplace-sport': 'attributes_sport',
      'marketplace-children': 'attributes_children',
      'marketplace-head-to-toe': 'attributes_head_to_toe',
      'marketplace-machines-tools': 'attributes_machines_tools',
      'marketplace-media': 'attributes_media',
      'marketplace-musical-instruments': 'attributes_musical_instruments',
      'marketplace-literature': 'attributes_literature',
      'marketplace-pets': 'attributes_pets',
      'marketplace-other': 'attributes_other',
      'marketplace-giving-away': 'attributes_giving_away',
      'jobs-professional': 'attributes_professional',
      'jobs-home-maintenance': 'attributes_home_maintenance',
      'jobs-personal-care-wellness': 'attributes_personal_care_wellness',
      'jobs-events-entertainment': 'attributes_events_entertainment',
      'jobs-education-training': 'attributes_education_training',
      'jobs-transport-delivery': 'attributes_transport_delivery',
    };

    const key = `${listing.category}-${listing.subcategory}`;
    return (
      tableMap[key] || `attributes_${listing.subcategory.replace(/-/g, '_')}`
    );
  }

  private loadWishlistStatus(listings: Listing[]): void {
    if (!this.authService.isLoggedIn() || !listings.length) {
      return;
    }

    const listingsForWishlist = listings
      .map((listing) => ({
        id: listing.id.toString(),
        table_name: listing.table_name || '',
      }))
      .filter((item) => item.table_name);

    if (listingsForWishlist.length === 0) {
      return;
    }

    this.wishlistService
      .checkWishlistStatus(listingsForWishlist)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.warn('Failed to load wishlist status:', error);
          return of({});
        })
      )
      .subscribe();
  }

  private formatCategoryName(category: string): string {
    const categoryMap: { [key: string]: string } = {
      vehicles: 'Vehicles',
      'real-estate': 'Real Estate',
      marketplace: 'Marketplace',
      jobs: 'Jobs',
    };
    return categoryMap[category] || category;
  }

  private formatSubcategoryName(subcategory: string): string {
    if (!subcategory) return '';
    return subcategory
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private getDefaultImage(category: string): string {
    const defaultImages: { [key: string]: string } = {
      vehicles: '/assets/images/listing-placeholder/no-image-placeholder.png',
      'real-estate':
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      marketplace:
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      jobs: '/assets/images/listing-placeholder/no-image-placeholder.png',
    };
    return (
      defaultImages[category] ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  }

  onListingClick(listing: Listing): void {
    if (listing.isTouchScrolling || listing.touchMoved) {
      return;
    }

    if (listing.touchStartTime !== undefined) {
      const tapDuration = Date.now() - listing.touchStartTime;
      if (tapDuration > this.TAP_TIME_THRESHOLD) {
        return;
      }
    }

    if (listing.subcategory) {
      this.router.navigate([
        '/listing-details',
        listing.subcategory,
        listing.id,
      ]);
    } else {
      this.router.navigate(['/listing', listing.id]);
    }
  }

  onCategoryChange(): void {
    this.currentPage = 1;
    this.loadRecentListings();
  }

  onSortChange(): void {
    this.currentPage = 1;
    this.loadRecentListings();
  }

  onSearch(): void {
    if (this.searchKeyword.trim()) {
      this.filters = { ...this.filters };
      const category =
        this.selectedCategory === 'all' ? 'all' : this.selectedCategory;
      this.router.navigate([
        '/search',
        category,
        encodeURIComponent(this.searchKeyword.trim()),
      ]);
    }
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadRecentListings();
      window.scrollTo(0, 0);
    }
  }

  onPriceFilterChange(): void {
    this.currentPage = 1;
    this.loadRecentListings();
  }

  onToggleImagesOnly(): void {
    this.filters.has_images = this.filters.has_images ? undefined : true;
    this.currentPage = 1;
    this.loadRecentListings();
  }

  clearFilters(): void {
    this.filters = {};
    this.selectedCategory = 'all';
    this.selectedSort = 'newest';
    this.searchKeyword = '';
    this.currentPage = 1;
    this.loadRecentListings();
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;

    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, start + maxVisible - 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  canUseWishlist(): boolean {
    return this.wishlistService.canUseWishlist();
  }

  shouldShowWishlistButton(listing: Listing): boolean {
    if (!this.authService.isLoggedIn()) {
      return false;
    }

    const currentUserId = this.authService.getCurrentUserId();
    if (currentUserId && listing.seller?.user_id) {
      return currentUserId.toString() !== listing.seller.user_id.toString();
    }

    return true;
  }
}
